import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  VisibilityRounded,
  SearchRounded,
  InfoOutlined,
} from "@mui/icons-material";
import Table, { TableHeaderConfig } from "../components/Table";
import { api } from "../utils/api";
import { PCC_AUTH_URL } from "../constants/pcc";
import FilterBar, {
  FilterChipConfig,
  FilterGroupConfig,
} from "../components/FilterBar";
import dayjs from "dayjs";

const PatientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [genderFilters, setGenderFilters] = useState<string[]>([]);

  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchBirthdate, setSearchBirthdate] = useState("");
  const [nameError, setNameError] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);

  const validateName = (value: string): string => {
    if (!value) return "";
    if (/\s/.test(value)) return "Spaces are not allowed. Use only first name or last name.";
    return "";
  };

  const handleNameChange = (value: string) => {
    setSearchName(value);
    setNameError(validateName(value));
  };

  const SESSION_KEY = "pcc_patient_search";

  const handleSearchOpen = () => {
    setSearchName("");
    setSearchBirthdate("");
    setNameError("");
    setSearchError(null);
    setSearchDialogOpen(true);
  };

  const handleSearchClose = () => {
    setSearchDialogOpen(false);
  };

  const handleSearch = () => {
    const nameErr = validateName(searchName);
    if (nameErr) {
      setNameError(nameErr);
      return;
    }
    if (!searchName.trim() && !searchBirthdate) {
      setSearchError("Please enter a name or select a birthdate.");
      return;
    }

    const params: Record<string, string> = {};
    if (searchName.trim()) params.name = searchName.trim();
    if (searchBirthdate) params.birthdate = dayjs(searchBirthdate).format("YYYY-MM-DD");

    sessionStorage.removeItem("pcc_search_results");
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(params));

    const authUrl = PCC_AUTH_URL.replace("state=abc123", "state=search-patient");
    window.location.href = authUrl;
  };

  const fetchPatients = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.get("/pcc/patients-stored");
      const data = res.data;
      const uniqueMap = new Map();
      const rawData = Array.isArray(data)
        ? data
        : data?.data && Array.isArray(data.data)
          ? data.data
          : data
            ? [data]
            : [];

      rawData.forEach((item: any) => {
        if (!item) return;
        const patient =
          item.details?.patient ||
          item.patient ||
          (item.resourceType === "Patient" ? item : null);
        if (patient && (patient.id || patient._id)) {
          const id = patient.id || patient._id;
          if (!uniqueMap.has(id)) {
            uniqueMap.set(id, { ...patient, id });
          }
        }
      });

      const patientList = Array.from(uniqueMap.values());
      setPatients(patientList);
    } catch (err: any) {
      console.error("Error fetching patients:", err);
      setError(err.message || "Failed to fetch patients");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const getPatientName = (row: any) => {
    const name = row.name?.[0];
    if (!name) return "N/A";
    return `${name?.given?.join(" ") || ""} ${name?.family || ""}`.trim();
  };

  const processedPatients = useMemo(() => {
    return patients.filter((p) => {
      const name = getPatientName(p).toLowerCase();
      const id = (p.id || "").toLowerCase();
      const query = searchQuery.toLowerCase();

      const matchesSearch = name.includes(query) || id.includes(query);
      const matchesGender =
        genderFilters.length === 0 || genderFilters.includes(p.gender);

      return matchesSearch && matchesGender;
    });
  }, [patients, searchQuery, genderFilters]);

  const genderOptions = useMemo(
    () => [
      { label: "Male", value: "male" },
      { label: "Female", value: "female" },
      { label: "Other", value: "other" },
      { label: "Unknown", value: "unknown" },
    ],
    [],
  );

  const filterGroups: FilterGroupConfig[] = [
    {
      id: "gender",
      label: "Gender",
      options: genderOptions,
      selectedValues: genderFilters,
      onChange: (values) => setGenderFilters(values),
    },
  ];

  const filterChips: FilterChipConfig[] = genderFilters.map((g) => ({
    id: `gender-${g}`,
    label: `Gender: ${g}`,
    onDelete: () => setGenderFilters((prev) => prev.filter((v) => v !== g)),
  }));

  const headers: TableHeaderConfig[] = [
    {
      key: "nameLabel",
      label: "Patient Name",
      render: (row: any) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={700}
            onClick={() => navigate(`/patients/${row.id}`)}
            sx={{
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
              textDecoration: "underline",
            }}
          >
            {getPatientName(row)}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            sx={{ fontSize: "12px" }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              Id:{" "}
            </Box>
            {row.id}
          </Typography>
        </Box>
      ),
    },
    {
      key: "telecom",
      label: "Contact",
      render: (row: any) => {
        const phones = (row.telecom || [])
          .filter((t: any) => t.system === "phone")
          .slice(0, 2);

        if (!phones.length) {
          return (
            <Typography variant="body2" fontWeight={500}>
              N/A
            </Typography>
          );
        }

        return (
          <Box>
            {phones.map((p: any, idx: number) => {
              const label = p.use
                ? p.use.charAt(0).toUpperCase() + p.use.slice(1)
                : "";

              return (
                <Typography
                  key={idx}
                  variant="caption"
                  display="block"
                  fontWeight={500}
                >
                  {label && (
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {label}:{" "}
                    </Box>
                  )}
                  {p.value}
                </Typography>
              );
            })}
          </Box>
        );
      },
    },
    {
      key: "gender",
      label: "Gender",
      render: (row: any) => (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ textTransform: "capitalize" }}
        >
          {row.gender || "N/A"}
        </Typography>
      ),
    },
    {
      key: "birthDate",
      label: "Date of Birth",
      render: (row: any) => (
        <Typography variant="body2" fontWeight={500}>
          {row.birthDate ? dayjs(row.birthDate).format("DD MMM YYYY") : "N/A"}
        </Typography>
      ),
    },
    {
      key: "location",
      label: "City",
      render: (row: any) => {
        const addresses = (row.address || []).slice(0, 2);

        if (!addresses.length) {
          return (
            <Typography variant="body2" fontWeight={500}>
              N/A
            </Typography>
          );
        }

        return (
          <Box>
            {addresses.map((addr: any, idx: number) => {
              const label = addr.use
                ? addr.use.charAt(0).toUpperCase() + addr.use.slice(1)
                : "";

              return (
                <Typography
                  key={idx}
                  variant="caption"
                  display="block"
                  fontWeight={500}
                >
                  {label && (
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {label}:{" "}
                    </Box>
                  )}
                  {addr.city || "N/A"}
                </Typography>
              );
            })}
          </Box>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      render: (row: any) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityRounded sx={{ fontSize: "16px !important" }} />}
          onClick={() => navigate(`/patients/${row.id}`)}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.7rem",
            px: "8px",
            py: 0.5,
            borderColor: "divider",
          }}
        >
          View Report
        </Button>
      ),
    },
  ];

  const gridTemplate = "2.5fr 1.5fr 0.7fr 1fr 1.5fr 1fr";


  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <FilterBar
            filterGroups={filterGroups}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by name or ID..."
            chips={filterChips}
            onClearAll={() => {
              setSearchQuery("");
              setGenderFilters([]);
            }}
          />
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<SearchRounded sx={{ fontSize: "16px" }} />}
              onClick={handleSearchOpen}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "12px",
                py: 1,
                px: 3,
                whiteSpace: "nowrap",
              }}
            >
              Search Patient
            </Button>
            {/* <Button
              variant="contained"
              startIcon={<RocketLaunchRounded sx={{ fontSize: "16px" }} />}
              onClick={() => (window.location.href = PCC_AUTH_URL)}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "12px",
                py: 1,
                px: 3,
                whiteSpace: "nowrap",
              }}
            >
              Fetch new patients
            </Button> */}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Table
          headers={headers}
          data={processedPatients}
          gridTemplateColumns={gridTemplate}
          emptyState="No patients found matching your search."
          variant="cards"
        />
      )}

      {/* Search Patient Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={handleSearchClose}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          Search Patient
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "0px !important" }}>
          <Typography variant="body2" color="text.secondary">
            Please enter name (first or last only, no spaces) and/or date of birth.
          </Typography>

          {/* Name field */}
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={searchName}
            onChange={(e) => handleNameChange(e.target.value)}
            error={!!nameError}
            helperText={nameError}
            placeholder="First or Last Name (No space)"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title="Enter only a first name OR last name. Spaces are not allowed."
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlined fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Birthdate field */}
          <TextField
            label="Date of Birth"
            size="small"
            fullWidth
            type="date"
            value={searchBirthdate}
            onChange={(e) => setSearchBirthdate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Format sent to API: YYYY-MM-DD (e.g. 1968-05-02)"
          />

          {searchError && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {searchError}
            </Alert>
          )}

          <Alert severity="info" sx={{ borderRadius: 1 }}>
            You will be redirected to PointClickCare to authorize. Search results will appear after login.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleSearchClose}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1, borderColor: "divider" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSearch}
            variant="contained"
            disabled={!!nameError}
            startIcon={<SearchRounded sx={{ fontSize: "16px" }} />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1 }}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDashboard;
